/**
 * The half of the plan's Reports line that has no server behind it — named, not
 * mocked (G18).
 *
 * Task 45 found four such surfaces on `/policies`, Task 53 found seven on
 * `/data-risk`, and the answer both times was the same: put the absence on the page,
 * with the endpoint it would need, rather than draw a control that cannot work.
 *
 * ── Four of the six are gone, because the servers arrived (Task U19) ────────
 *
 * A panel of absences is only worth anything if it is **re-checked**, and this round
 * removed four bullets rather than adding one. Each was re-read in `src/` and in
 * `openapi.yaml` before it was deleted, and each is deleted for a reason that can be
 * pointed at:
 *
 *  - **`GET /reports` exists** — `listReports`, `src/modules/reports/routes.ts:262`,
 *    `openapi.yaml` `operationId: listReports`, offset-paged with a real `total`
 *    (G26, amendment A20). The screen reads it, and the tab-local list it used to
 *    keep instead is gone.
 *  - **`PATCH /report-schedules/{id}` exists** — `routes.ts:354`, `If-Match`
 *    mandatory. `ScheduleManager` sends `{ enabled }` through it.
 *  - **`DELETE /report-schedules/{id}` exists** — `routes.ts:420`, `204`, no
 *    precondition. `ScheduleManager` sends it, **and says what it really does**: it
 *    deletes the row rather than ending the schedule. See that file.
 *  - **The download writes its `wp_data_access_logs` row** — G25 put
 *    `writeDataAccessLog` into `grantDownload` (`reports/service.ts`), positioned
 *    after the scope re-check and before the URL is minted, with a failure there
 *    failing the download. The bullet that said no row was written was true when it
 *    was written and is now false. The page still asks for no access reason, and the
 *    sentence explaining *that* moved to the list, where the download control is,
 *    because it is no longer a statement about an absent endpoint.
 *
 * ── The two that are still true ────────────────────────────────────────────
 *
 *  1. **`GET /report-schedules` — no list.** `src/modules/schedules/routes.ts` mounts
 *     `POST` on the collection and `GET`/`PATCH`/`DELETE` on `/{id}`, and nothing
 *     that enumerates. So a schedule is reachable only through an id somebody already
 *     holds, and no browser can answer *"what standing exports is this company
 *     running?"*. That is why `ScheduleManager` works on one typed id rather than a
 *     table, and it is the one bullet here that is about an absent endpoint.
 *  2. **The wiring gap, not the wall.** Tasks 43, 44, 45 and 53 each hit "no employee
 *     directory" as the reason three id fields on this page were typed rather than
 *     picked; Task T2-A closed that wall (`GET /api/v1/employees`) and Task T2-B gave
 *     the web app a picker (`EmployeePicker`), but `CreateReportForm`'s
 *     `employee_ids`/`department_id` and `ScheduleForm`'s `recipients` have not been
 *     rewired to either — rewiring them is a separate task, out of scope here. So the
 *     bullet stays, corrected: what is true today is the wiring gap.
 *
 * **No control lives in this panel and specifically no disabled one.** A greyed
 * button asserts "this could do something, but not for you"; here nothing could, for
 * anybody, in this build.
 */

import type { ReactNode } from 'react';

import { useI18n } from '../../i18n';

export function AbsentEndpoints(): ReactNode {
  const { t } = useI18n();
  return (
    <section className="wp-section" aria-labelledby="k8-absent-heading" data-testid="k8-absent">
      <h2 className="wp-section__title" id="k8-absent-heading">
        {t('k8.reports.absent.title')}
      </h2>
      <p>{t('k8.reports.absent.body')}</p>
      <ul className="wp-list">
        <li data-testid="k8-absent-sched-list">{t('k8.reports.absent.noScheduleList')}</li>
        <li data-testid="k8-absent-directory">{t('k8.reports.absent.noDirectory')}</li>
      </ul>
      <p className="wp-small wp-muted" data-testid="k8-absent-nocontrols">
        {t('k8.reports.absent.noControls')}
      </p>
    </section>
  );
}
